import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { focusElement } from 'platform/utilities/ui';

import {
  VaPagination,
  VaRadio,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';

/**
 * One radio option, built by the parent from its own API response
 * @typedef {Object} FacilityRadioOption
 * @property {string} id Unique; used as the element ID and React key
 * @property {string} label Shown to the user
 * @property {string} value Passed to `onRadioSelect` and compared with `selected`
 */

export const PER_PAGE = 10;
export const FACILITY_NOT_LISTED = 'My facility is not listed';

/**
 * Whether `FacilitySearchResults` shows the radio list for these props.
 * Exported so parents can place validation errors without copying the rule.
 * @param {object} props
 * @param {number} props.resultCount Results on the current page
 * @param {string} [props.resultsQuery]
 * @param {boolean} [props.isSearching]
 * @param {boolean} [props.showNotListed]
 * @returns {boolean}
 */
export function isRadioListShown({
  resultCount,
  resultsQuery,
  isSearching,
  showNotListed = false,
}) {
  const searchFinished = !!resultsQuery && !isSearching;
  // "Not listed" stays available even when a search finds nothing
  return searchFinished && (resultCount > 0 || showNotListed);
}

/**
 * Status region describing the search, plus the results radio list and pagination.
 * API-agnostic: parents map their response into these props and own the page state.
 * @param {object} props
 * @param {FacilityRadioOption[]} props.results Results on the current page
 * @param {number} props.pageNum Current page, 1-based
 * @param {number} props.totalCount Total results across all pages
 * @param {(pageNum: number) => void} props.onPageChange
 * @param {(value: string) => void} props.onRadioSelect Receives the option's `value`
 * @param {string} props.radioLabel e.g. `"Select school facility"`
 * @param {string} props.noResultsMessage Shown when a search returns nothing
 * @param {boolean} [props.isSearching]
 * @param {string} [props.selected] `value` of the current selection
 * @param {string} [props.resultsQuery] Query that `results` belong to. Empty before a completed search, which renders nothing.
 * @param {string} [props.error] Validation message for the radio group
 * @param {boolean} [props.showNotListed] Add a "My facility is not listed" option
 */
export function FacilitySearchResults({
  results,
  pageNum,
  totalCount,
  onPageChange,
  onRadioSelect,
  radioLabel,
  noResultsMessage,
  isSearching,
  selected,
  resultsQuery,
  error,
  showNotListed = false,
}) {
  const currentPageCount = results.length;
  const totalPages = Math.ceil(totalCount / PER_PAGE);
  const rangeStart = (pageNum - 1) * PER_PAGE + 1;
  const rangeEnd = rangeStart + currentPageCount - 1;
  const showDescription = !!resultsQuery && !isSearching;
  const hasResults = showDescription && currentPageCount > 0;
  const showRadio = isRadioListShown({
    resultCount: currentPageCount,
    resultsQuery,
    isSearching,
    showNotListed,
  });

  // The description remounts after each search or page change, so focus it then
  const focusOnMount = useCallback(element => {
    if (element) focusElement('#facility-search-description p');
  }, []);

  return (
    <div>
      <div id="facility-search-description">
        {/* Always mounted so the loading message is announced */}
        <div role="status">
          {isSearching && (
            <span className="vads-u-visibility--screen-reader">
              Loading results
            </span>
          )}
        </div>
        {showDescription && (
          <p
            ref={focusOnMount}
            className="vads-u-padding-bottom--2 vads-u-border-bottom--1px vads-u-border-color--divider"
            tabIndex="-1"
          >
            {hasResults
              ? `Showing ${rangeStart}-${rangeEnd} of ${totalCount} results for "${resultsQuery}"`
              : noResultsMessage}
          </p>
        )}
      </div>
      {isSearching && (
        // Visual only. The status region above makes the announcement.
        <div aria-hidden="true">
          <va-loading-indicator message="Loading results..." />
        </div>
      )}
      {showRadio && (
        <div id="facility-search-results">
          <VaRadio
            class="vads-u-margin-y--2"
            label={radioLabel}
            labelHeaderLevel="4"
            error={error || undefined}
            onVaValueChange={e => onRadioSelect(e.detail.value)}
            required
            uswds
          >
            {results.map(option => (
              <va-radio-option
                key={option.id}
                id={option.id}
                label={option.label}
                value={option.value}
                name="primary"
                checked={selected === option.value}
                uswds
              />
            ))}
            {showNotListed && (
              <va-radio-option
                id="facility-not-listed"
                label={FACILITY_NOT_LISTED}
                value={FACILITY_NOT_LISTED}
                name="primary"
                checked={selected === FACILITY_NOT_LISTED}
                uswds
              />
            )}
          </VaRadio>
          {totalCount > PER_PAGE && (
            <VaPagination
              onPageSelect={e => onPageChange(e.detail.page)}
              page={pageNum}
              pages={totalPages}
              maxPageListLength={5}
              showLastPage
            />
          )}
        </div>
      )}
    </div>
  );
}

FacilitySearchResults.propTypes = {
  noResultsMessage: PropTypes.string.isRequired,
  pageNum: PropTypes.number.isRequired,
  radioLabel: PropTypes.string.isRequired,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRadioSelect: PropTypes.func.isRequired,
  error: PropTypes.string,
  isSearching: PropTypes.bool,
  resultsQuery: PropTypes.string,
  selected: PropTypes.string,
  showNotListed: PropTypes.bool,
};
